// 1. React imports
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react'

// 2. External library imports
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Menu,
  TablePagination,
  Avatar,
} from '@mui/material'
import {
  Search,
  ArrowDropDown,
  KeyboardArrowUp,
  KeyboardArrowDown,
  ErrorOutline,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material'
import dayjs, { Dayjs } from 'dayjs'
import { useSearchParams } from 'react-router-dom'

// 3. Internal imports
import { useSentryEvents } from '@/hooks/react-query/useSentryEvents'
import { usePriorityCounts } from '@/hooks/react-query/usePriorityCounts'
import { useSeverityPercentage } from '@/hooks/react-query/useSeverityPercentage'
import { useTrendGraph } from '@/hooks/react-query/useTrendGraph'
import { useExportIssues } from '@/hooks/react-query/useExportIssues'
import Loading from '@/components/shared/layout/Loading'
import { ErrorDisplay } from '@/components/shared/ErrorBoundary'
import { TrendGraph } from '@/components/shared/TrendGraph'
import { DateRangePicker } from '@/components/shared/DateRangePicker'
import { IssueDetailPanel } from '@/components/shared/IssueDetailPanel'
import { generateExportFilename, exportApiDataToCSV, exportApiDataToXLSX } from '@/utils/exportUtils'
import { SentryIssue } from '@/types/sentryIssue.type'
import { DaylightDateFormat } from '@/constants/DaylightDateFormat'
import {
  SENTRY_PROJECT_APEX_CLIENT,
  SENTRY_PROJECT_DISPATCH_MOBILE,
  SENTRY_PROJECT_DOCK_AUTOMATION,
} from '@/constants/sentryProjects'
import { handleExportError } from '@/utils/exportErrorHandler'
import { zIndexes } from '@/utils/zIndexes'
import { displayMessage, toastMessage } from '@/constants/strings'
import { toast } from '@/utils/snackbarHelper'
import csvIcon from '../assets/csv-icon.svg'
import xlsxIcon from '../assets/xlsx-icon.svg'
// Format numbers with K suffix
const formatNumber = (num: number): string => {
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}k`
  }
  return num.toString()
}

export const Component = () => {
  // Get dashboard from URL query parameter
  const [searchParams] = useSearchParams()
  const dashboard = searchParams.get('dashboard') || 'apex-client'

  // Determine project ID based on dashboard
  const projectId = useMemo(() => {
    switch (dashboard) {
      case 'apex-client':
        return SENTRY_PROJECT_APEX_CLIENT
      case 'dispatch-mobile':
        return SENTRY_PROJECT_DISPATCH_MOBILE
      case 'dock-automation':
        return SENTRY_PROJECT_DOCK_AUTOMATION
      default:
        return SENTRY_PROJECT_APEX_CLIENT
    }
  }, [dashboard])

  const [searchQuery, setSearchQuery] = useState('')
  const [envFilter, setEnvFilter] = useState('all')
  // Actual date range used for API calls (only updated on OK button click)
  // Default: yesterday to today (24 hours period)
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    dayjs().subtract(1, 'day'), // Yesterday
    dayjs(), // Today
  ])
  const [selectedIssue, setSelectedIssue] = useState<SentryIssue | null>(null)
  const [isDetailPanelOpen, setIsDetailPanelOpen] = useState(false)

  const [rowsPerPage, setRowsPerPage] = useState(25) // Default to 25 to match API
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null)
  const [orderBy, setOrderBy] = useState<
    'title' | 'lastSeen' | 'age' | 'totalEvents' | 'affectedUsers' | 'priority' | null
  >(null)
  const [order, setOrder] = useState<'asc' | 'desc'>('asc')
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [allIssues, setAllIssues] = useState<SentryIssue[]>([])
  const [currentPage, setCurrentPage] = useState(0)
  const [latestNextCursor, setLatestNextCursor] = useState<string | null>(null) // Track the latest nextCursor from API response
  const processedResponseRef = useRef<string | null>(null) // Track which cursor we've already processed
  const lastProcessedDataUpdatedAtRef = useRef<number | null>(null) // Track the last dataUpdatedAt we processed
  const lastProcessedNextCursorRef = useRef<string | null>(null) // Track the last nextCursor we processed

  // Pass environment filter and date range to the hook
  // Extract stable date values to prevent infinite loops in useEffect dependencies
  const startDateValue = dateRange[0]?.valueOf() ?? null
  const endDateValue = dateRange[1]?.valueOf() ?? null

  // Memoize startDate and endDate to prevent unnecessary re-renders
  const startDate = useMemo(() => {
    const date = dateRange[0] ? dateRange[0].toDate() : null
    return date
  }, [dateRange])
  const endDate = useMemo(() => {
    const date = dateRange[1] ? dateRange[1].toDate() : null
    return date
  }, [dateRange])

  // Fetch issues from workstream-specific endpoint (or general endpoint if "all")
  // For Overview page, pass null for workstream to avoid culprit query filtering
  const {
    data: response,
    isLoading,
    isError,
    dataUpdatedAt,
  } = useSentryEvents(envFilter, startDate, endDate, nextCursor, null, searchQuery, projectId)

  // Export issues mutation
  const { mutate: exportIssues, isPending: isExporting } = useExportIssues()

  // Fetch priority counts from dedicated API endpoint
  const { data: priorityCountsData, isLoading: isLoadingPriorityCounts } = usePriorityCounts({
    startDate: startDate
      ? dayjs(startDate).format(DaylightDateFormat.ISO_DATE_TIME_WITHOUT_TIMEZONE)
      : dayjs().subtract(1, 'day').format(DaylightDateFormat.ISO_DATE_TIME_WITHOUT_TIMEZONE),
    endDate: endDate
      ? dayjs(endDate).format(DaylightDateFormat.ISO_DATE_TIME_WITHOUT_TIMEZONE)
      : dayjs().format(DaylightDateFormat.ISO_DATE_TIME_WITHOUT_TIMEZONE),
    project: projectId,
    workstream: null, // No workstream filter for Overview
    environment: envFilter === 'all' ? undefined : envFilter,
    searchQuery, // Pass searchQuery for Dispatch Mobile and Dock Automation dashboards
  })

  // Fetch severity percentage from dedicated API endpoint
  const { data: severityPercentageData, isLoading: isLoadingSeverityPercentage } = useSeverityPercentage({
    startDate: startDate
      ? dayjs(startDate).format(DaylightDateFormat.ISO_DATE_TIME_WITHOUT_TIMEZONE)
      : dayjs().subtract(1, 'day').format(DaylightDateFormat.ISO_DATE_TIME_WITHOUT_TIMEZONE),
    endDate: endDate
      ? dayjs(endDate).format(DaylightDateFormat.ISO_DATE_TIME_WITHOUT_TIMEZONE)
      : dayjs().format(DaylightDateFormat.ISO_DATE_TIME_WITHOUT_TIMEZONE),
    project: projectId,
    workstream: null, // No workstream filter for Overview
    environment: envFilter === 'all' ? undefined : envFilter,
    searchQuery, // Pass searchQuery for all dashboards
  })

  // Memoize trend graph parameters to prevent unnecessary re-renders
  const trendGraphParams = useMemo(
    () => ({
      projectId,
      statsPeriod: '7d' as const,
      environment: envFilter === 'all' ? undefined : envFilter,
      workstream: null, // No workstream filter for Overview
    }),
    [projectId, envFilter]
  )

  // Fetch trend graph data for all priority levels (7 days by default)
  // Only pass environment if it's explicitly selected (not null/"all")
  const { data: trendGraphHigh } = useTrendGraph({
    ...trendGraphParams,
    severity: 'high',
  })

  const { data: trendGraphMedium } = useTrendGraph({
    ...trendGraphParams,
    severity: 'medium',
  })

  const { data: trendGraphLow } = useTrendGraph({
    ...trendGraphParams,
    severity: 'low',
  })

  const { data: trendGraphTotal } = useTrendGraph({
    ...trendGraphParams,
    severity: 'total',
  })

  // Extract pagination and x-hits from response
  const pagination = response?.pagination
  const xHits = response?.['x-hits']

  // Reset accumulated issues when filters change
  // Use stable date values to prevent infinite loops
  useEffect(() => {
    // Reset when filters change (not when cursor changes for pagination)
    setAllIssues([])
    setCurrentPage(0)
    setNextCursor(null)
    setLatestNextCursor(null)
    processedResponseRef.current = null // Reset processed responses tracker
    lastProcessedDataUpdatedAtRef.current = null // Reset last processed dataUpdatedAt
    lastProcessedNextCursorRef.current = null // Reset last processed nextCursor
  }, [envFilter, startDateValue, endDateValue, dashboard])

  // Update latestNextCursor from response and accumulate issues
  // Use a combination of dataUpdatedAt and nextCursor to track unique responses
  useEffect(() => {
    if (!response || !response.issues || response.issues.length === 0) return

    // Create a unique identifier for this response
    const currentCursor = nextCursor || 'initial'

    // Skip if we've already processed this exact combination of dataUpdatedAt and nextCursor
    if (
      lastProcessedDataUpdatedAtRef.current === dataUpdatedAt &&
      lastProcessedNextCursorRef.current === currentCursor
    ) {
      return
    }

    // Mark this response as processed BEFORE any state updates
    lastProcessedDataUpdatedAtRef.current = dataUpdatedAt
    lastProcessedNextCursorRef.current = currentCursor

    // Create a unique key for this response
    const responseKey = `${currentCursor}-${dataUpdatedAt}`
    processedResponseRef.current = responseKey
    // Batch all state updates together to minimize re-renders
    if (nextCursor) {
      // Append new issues for next page (avoid duplicates)
      setAllIssues((prev) => {
        const existingIds = new Set(prev.map((issue) => issue.id))
        const newIssues = response.issues.filter((issue) => !existingIds.has(issue.id))
        return [...prev, ...newIssues]
      })
    } else {
      // First page - replace all issues
      setAllIssues(response.issues)
    }

    // Update latest cursor from response
    if (response.pagination?.nextCursor) {
      setLatestNextCursor(response.pagination.nextCursor)
    } else {
      setLatestNextCursor(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdatedAt, nextCursor]) // Only depend on dataUpdatedAt and nextCursor - response is accessed from closure

  // Use allIssues for filtering (accumulated from pagination)
  const issuesToFilter = useMemo(() => {
    const issues = allIssues.length > 0 ? allIssues : response?.issues || []
    return issues
  }, [allIssues, response?.issues])

  // Helper function to parse lastSeen strings to comparable numbers
  // Handles both ISO timestamp strings (e.g., "2026-01-15T06:49:42Z") and relative time strings (e.g., "19m ago")
  const parseLastSeen = useCallback((lastSeen: string): number => {
    if (!lastSeen) return 0

    // Check if it's an ISO timestamp string
    if (lastSeen.includes('T') && (lastSeen.includes('Z') || lastSeen.includes('+'))) {
      const date = new Date(lastSeen)
      if (!isNaN(date.getTime())) {
        const now = new Date()
        return Math.floor((now.getTime() - date.getTime()) / (1000 * 60)) // Return minutes ago
      }
    }

    // Handle relative time strings (backward compatibility)
    const lower = lastSeen.toLowerCase()
    if (lower.includes('just now')) return 0
    const minutesMatch = lower.match(/(\d+)m ago/)
    if (minutesMatch) return parseInt(minutesMatch[1], 10)
    const hoursMatch = lower.match(/(\d+)h ago/)
    if (hoursMatch) return parseInt(hoursMatch[1], 10) * 60
    const daysMatch = lower.match(/(\d+)d ago/)
    if (daysMatch) return parseInt(daysMatch[1], 10) * 60 * 24
    return 0
  }, [])

  // Helper function to parse age strings to comparable numbers (converts to minutes)
  const parseAge = useCallback((age: string): number => {
    if (!age) return 0
    const lower = age.toLowerCase().trim()

    // Handle "today"
    if (lower === 'today') return 0

    // Check longer patterns first to avoid false matches
    // Use word boundary \b or end-of-string $ to ensure exact unit match

    // Handle months: "1 month", "2 months", "2mo" - check before minutes!
    const monthsMatch = lower.match(/(\d+)\s*(months?|mo\b)/)
    if (monthsMatch) return parseInt(monthsMatch[1], 10) * 30 * 24 * 60

    // Handle years: "1 year", "2 years", "1y" - check before hours/days
    const yearsMatch = lower.match(/(\d+)\s*(years?|y\b)/)
    if (yearsMatch) return parseInt(yearsMatch[1], 10) * 365 * 24 * 60

    // Handle weeks: "2 weeks", "1 week", "2w"
    const weeksMatch = lower.match(/(\d+)\s*(weeks?|w\b)/)
    if (weeksMatch) return parseInt(weeksMatch[1], 10) * 7 * 24 * 60

    // Handle days: "3 days", "1 day", "3d"
    const daysMatch = lower.match(/(\d+)\s*(days?|d\b)/)
    if (daysMatch) return parseInt(daysMatch[1], 10) * 24 * 60

    // Handle hours: "2 hours", "1 hour", "2h"
    const hoursMatch = lower.match(/(\d+)\s*(hours?|h\b)/)
    if (hoursMatch) return parseInt(hoursMatch[1], 10) * 60

    // Handle minutes: "8 minutes", "1 minute", "8m" - check last to avoid matching "2mo"
    const minutesMatch = lower.match(/(\d+)\s*(minutes?|m\b)/)
    if (minutesMatch) return parseInt(minutesMatch[1], 10)

    return 0
  }, [])

  // Memoize filtered issues - filter by search and environment only
  // Date range filtering is handled by the API
  const filteredIssues = useMemo(() => {
    if (!issuesToFilter || issuesToFilter.length === 0) {
      return []
    }
    const filtered = issuesToFilter.filter((issue: SentryIssue) => {
      const matchesEnv =
        envFilter === 'all' ||
        !issue.enrichedEnvironment ||
        issue.enrichedEnvironment.toLowerCase() === envFilter.toLowerCase() ||
        issue.enrichedEnvironment.toLowerCase() === ''

      // Trust the API's filtering for:
      // - Workstream (using culprit query pattern)
      // - Date range (using start/end parameters)
      // - Search query (using title:"searchTerm" in query parameter)
      return matchesEnv
    })

    // Apply sorting only if orderBy is set
    if (!orderBy) {
      return filtered
    }

    const sorted = [...filtered].sort((a, b) => {
      let aValue: string | number
      let bValue: string | number

      switch (orderBy) {
        case 'title':
          aValue = a.title?.toLowerCase() || ''
          bValue = b.title?.toLowerCase() || ''
          break
        case 'lastSeen':
          // Parse "Xm ago", "Xh ago", "Xd ago" to comparable values (minutes ago)
          // Smaller values = more recent, so for descending (most recent first), we want ascending sort
          aValue = parseLastSeen(a.lastSeen)
          bValue = parseLastSeen(b.lastSeen)
          // For lastSeen, descending means most recent first (smallest minutes ago first)
          // So we need to reverse the comparison logic
          if (aValue < bValue) {
            return order === 'desc' ? -1 : 1 // Desc: smaller (more recent) first
          }
          if (aValue > bValue) {
            return order === 'desc' ? 1 : -1 // Desc: larger (older) last
          }
          return 0
        case 'age':
          // Parse age strings like "Today", "1d", "3w", "2mo"
          aValue = parseAge(a.age)
          bValue = parseAge(b.age)
          break
        case 'totalEvents':
          aValue = a.totalEvents || 0
          bValue = b.totalEvents || 0
          break
        case 'affectedUsers':
          aValue = a.affectedUsers || 0
          bValue = b.affectedUsers || 0
          break
        case 'priority': {
          // Map priority to numeric values: high = 3, medium = 2, low = 1
          const priorityMap: Record<string, number> = {
            high: 3,
            medium: 2,
            low: 1,
          }
          aValue = (a.priority && priorityMap[a.priority]) || 0
          bValue = (b.priority && priorityMap[b.priority]) || 0
          break
        }
        default:
          return 0
      }

      if (aValue < bValue) {
        return order === 'asc' ? -1 : 1
      }
      if (aValue > bValue) {
        return order === 'asc' ? 1 : -1
      }
      return 0
    })

    return sorted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, envFilter, issuesToFilter, orderBy, order, parseLastSeen, parseAge, response])

  // Calculate priority counts and trends
  // Use API data from usePriorityCounts for counts and useSeverityPercentage for percentage changes
  const priorityStats = useMemo(() => {
    // Use API data if available, fallback to filtered issues count
    const highCount =
      priorityCountsData?.priorityCounts?.high ??
      filteredIssues.filter((issue: SentryIssue) => issue.priority === 'high').length
    const mediumCount =
      priorityCountsData?.priorityCounts?.medium ??
      filteredIssues.filter((issue: SentryIssue) => issue.priority === 'medium').length
    const lowCount =
      priorityCountsData?.priorityCounts?.low ??
      filteredIssues.filter((issue: SentryIssue) => issue.priority === 'low').length
    const totalCount = priorityCountsData?.totalIssues ?? filteredIssues.length

    // Use API percentage changes if available, fallback to mock data
    const highChange = severityPercentageData?.['Severity-counts-Comparison']?.high?.percentageChange ?? 12
    const mediumChange = severityPercentageData?.['Severity-counts-Comparison']?.medium?.percentageChange ?? -5
    const lowChange = severityPercentageData?.['Severity-counts-Comparison']?.low?.percentageChange ?? -3
    const totalChange = severityPercentageData?.['Severity-counts-Comparison']?.total?.percentageChange ?? 8

    return {
      high: { count: highCount, change: highChange },
      medium: { count: mediumCount, change: mediumChange },
      low: { count: lowCount, change: lowChange },
      total: { count: totalCount, change: totalChange },
    }
  }, [filteredIssues, priorityCountsData, severityPercentageData])

  // Handle sort request
  const handleRequestSort = (property: 'title' | 'lastSeen' | 'age' | 'totalEvents' | 'affectedUsers' | 'priority') => {
    if (orderBy === property) {
      // Toggle between asc and desc
      if (order === 'asc') {
        setOrder('desc')
      } else {
        // If already desc, reset to no sort
        setOrderBy(null)
        setOrder('asc')
      }
    } else {
      // New column - set to ascending
      setOrderBy(property)
      setOrder('asc')
    }
  }

  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget)
  }

  const handleExportClose = () => {
    setExportMenuAnchor(null)
  }

  // Export handlers
  const handleExportCSV = () => {
    exportIssues(
      {
        environment: envFilter,
        startDate,
        endDate,
        workstream: null, // No workstream filter for Overview
        searchQuery,
      },
      {
        onSuccess: (data) => {
          try {
            if (data.data.length === 0) {
              toast('No issues to export', { variant: 'info' })
              handleExportClose()
              return
            }

            const filename = generateExportFilename('csv', dashboard || 'all', startDate, endDate)
            exportApiDataToCSV(data.data, filename)
            toast(toastMessage.sentry.exportSuccess, { variant: 'success' })
            handleExportClose()
          } catch (error) {
            handleExportError(error, 'CSV file generation')
          }
        },
        onError: (error) => {
          handleExportError(error, 'CSV API call')
        },
      }
    )
  }

  const handleExportXLSX = () => {
    exportIssues(
      {
        environment: envFilter,
        startDate,
        endDate,
        workstream: null, // No workstream filter for Overview
        searchQuery,
      },
      {
        onSuccess: (data) => {
          try {
            if (data.data.length === 0) {
              toast('No issues to export', { variant: 'info' })
              handleExportClose()
              return
            }

            const filename = generateExportFilename('xlsx', dashboard || 'all', startDate, endDate)
            exportApiDataToXLSX(data.data, filename)
            toast(toastMessage.sentry.exportSuccess, { variant: 'success' })
            handleExportClose()
          } catch (error) {
            handleExportError(error, 'XLSX file generation')
          }
        },
        onError: (error) => {
          handleExportError(error, 'XLSX API call')
        },
      }
    )
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    if (newPage > currentPage) {
      // Going to next page
      // Use latestNextCursor from the most recent response
      if (pagination?.hasMore && latestNextCursor) {
        // Trigger API call with cursor to fetch next page
        setNextCursor(latestNextCursor)
        setCurrentPage(newPage)
      } else if (pagination?.hasMore && pagination?.nextCursor) {
        // Fallback to pagination.nextCursor if latestNextCursor is not set yet
        setNextCursor(pagination.nextCursor)
        setCurrentPage(newPage)
      } else {
        // No more data available or no cursor - just navigate through local data
        setCurrentPage(newPage)
      }
    } else if (newPage < currentPage) {
      // Previous page - use already loaded data
      setCurrentPage(newPage)
    } else {
      // Same page
      setCurrentPage(newPage)
    }
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10)
    setRowsPerPage(newRowsPerPage)
    setCurrentPage(0)
    setNextCursor(null) // Reset cursor when changing page size
    setAllIssues([]) // Reset accumulated issues
  }

  // Calculate paginated issues from filtered data
  const paginatedIssues = useMemo(() => {
    const startIndex = currentPage * rowsPerPage
    const endIndex = startIndex + rowsPerPage
    return filteredIssues.slice(startIndex, endIndex)
  }, [filteredIssues, currentPage, rowsPerPage])

  // Calculate total count for pagination display using x-hits from API
  const totalCount = useMemo(() => {
    // Use x-hits from API response, fallback to filteredIssues.length
    const count = xHits ?? filteredIssues.length
    return count
  }, [xHits, filteredIssues.length])

  // Check if next button should be enabled
  const canGoToNextPage = useMemo(() => {
    // Check if we have more data locally (in filteredIssues)
    const hasMoreLocalData = (currentPage + 1) * rowsPerPage < filteredIssues.length

    // Can go to next page if:
    // 1. We have more local data to show, OR
    // 2. hasMore is true (meaning we can fetch more from API)
    return hasMoreLocalData || pagination?.hasMore === true
  }, [currentPage, rowsPerPage, filteredIssues.length, pagination?.hasMore])

  // Error state - show full page error
  if (isError && !isLoading) {
    return (
      <ErrorDisplay
        primaryMessage={displayMessage.sentry.error}
        secondaryMessage={displayMessage.sentry.errorSubtext}
        showReloadOption
      />
    )
  }

  // Show page structure even during loading
  // Loading indicators will be shown within components
  return (
    <Box
      sx={{
        width: '100%',
        px: { xs: 2, sm: 3 },
        paddingBottom: { xs: 2, sm: 3 },
        paddingTop: { xs: 2, sm: 3 },
        backgroundColor: '#fafafa',
      }}
    >
      {/* Title and Count */}
      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
        {/* Priority Cards */}
        <Box
          sx={{
            display: 'flex',
            gap: { xs: 1.5, sm: 2 },
            mb: { xs: 2, sm: 3 },
            flexWrap: 'wrap',
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          {/* High Priority Card */}
          <Paper
            sx={{
              flex: 1,
              minWidth: { xs: '100%', sm: 200, md: 200 },
              borderRadius: 2,
              boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
              backgroundColor: '#FFFFFF',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Vertical colored bar on left edge */}
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '6px',
                backgroundColor: '#D32F2F',
              }}
            />
            <Box sx={{ p: 2, pl: 3 }}>
              {/* Top row: Title and Icon Button */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '18px',
                    fontStyle: 'bold',
                    fontWeight: 700,
                    color: '#666',
                  }}
                >
                  High Priority
                </Typography>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    backgroundColor: '#FEF3F2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #FECECE',
                  }}
                >
                  <ArrowUpward sx={{ fontSize: 24, color: '#B81C1C' }} />
                </Box>
              </Box>

              {/* Number and Trend Indicator Row */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 2,
                  mb: 1.5,
                }}
              >
                {isLoadingPriorityCounts || isLoadingSeverityPercentage ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Loading size="2rem" />
                  </Box>
                ) : (
                  <>
                    <Typography
                      sx={{
                        fontSize: '32px',
                        fontWeight: 700,
                        color: '#333',
                        lineHeight: 1.2,
                      }}
                    >
                      {priorityStats.high.count}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                      <Typography
                        sx={{
                          fontSize: '20px',
                          fontWeight: 600,
                          color: priorityStats.high.change > 0 ? '#D32F2F' : '#2E7D32',
                        }}
                      >
                        {priorityStats.high.change > 0 ? (
                          <ArrowUpward sx={{ fontSize: 20 }} />
                        ) : (
                          <ArrowDownward sx={{ fontSize: 20 }} />
                        )}
                        {Math.abs(priorityStats.high.change)}%
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>

              {/* Graph and Trend Label Row */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: 1,
                  width: '65%',
                }}
              >
                <Box sx={{ flex: 1, height: 50 }}>
                  <TrendGraph data={trendGraphHigh?.data} color="#D32F2F" gradientId="highPriorityGradient" />
                </Box>
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: 400,
                    color: '#999',
                    mb: 0.5,
                  }}
                >
                  Last 7d trend
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Medium Priority Card */}
          <Paper
            sx={{
              flex: 1,
              minWidth: { xs: '100%', sm: 200, md: 200 },
              borderRadius: 2,
              boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
              backgroundColor: '#FFFFFF',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Vertical colored bar on left edge */}
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '6px',
                backgroundColor: '#A16107',
              }}
            />
            <Box sx={{ p: 2, pl: 3 }}>
              {/* Top row: Title and Icon Button */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '18px',
                    fontStyle: 'bold',
                    fontWeight: 700,
                    color: '#666',
                  }}
                >
                  Medium Priority
                </Typography>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    backgroundColor: '#FEFCE7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #EAE1A0',
                  }}
                >
                  <Typography sx={{ fontSize: '36px', fontWeight: 700, color: '#A16107' }}>=</Typography>
                </Box>
              </Box>

              {/* Number and Trend Indicator Row */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 2,
                  mb: 1.5,
                }}
              >
                {isLoadingPriorityCounts || isLoadingSeverityPercentage ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Loading size="2rem" />
                  </Box>
                ) : (
                  <>
                    <Typography
                      sx={{
                        fontSize: '32px',
                        fontWeight: 700,
                        color: '#333',
                        lineHeight: 1.2,
                      }}
                    >
                      {priorityStats.medium.count}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                      <Typography
                        sx={{
                          fontSize: '20px',
                          fontWeight: 600,
                          color: priorityStats.medium.change > 0 ? '#D32F2F' : '#2E7D32',
                        }}
                      >
                        {priorityStats.medium.change > 0 ? (
                          <ArrowUpward sx={{ fontSize: 20 }} />
                        ) : (
                          <ArrowDownward sx={{ fontSize: 20 }} />
                        )}
                        {Math.abs(priorityStats.medium.change)}%
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>

              {/* Graph and Trend Label Row */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: 1,
                  width: '65%',
                }}
              >
                <Box sx={{ flex: 1, height: 50 }}>
                  <TrendGraph data={trendGraphMedium?.data} color="#A16107" gradientId="mediumPriorityGradient" />
                </Box>
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: 400,
                    color: '#999',
                    mb: 0.5,
                  }}
                >
                  Last 7d trend
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Low Priority Card */}
          <Paper
            sx={{
              flex: 1,
              minWidth: { xs: '100%', sm: 200, md: 200 },
              borderRadius: 2,
              boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
              backgroundColor: '#FFFFFF',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Vertical colored bar on left edge */}
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '6px',
                backgroundColor: '#2050D8',
              }}
            />
            <Box sx={{ p: 2, pl: 3 }}>
              {/* Top row: Title and Icon Button */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '18px',
                    fontStyle: 'bold',
                    fontWeight: 700,
                    color: '#666',
                  }}
                >
                  Low Priority
                </Typography>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    backgroundColor: '#F0F6FF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #C6DFFF',
                  }}
                >
                  <ArrowDownward sx={{ fontSize: 24, color: '#2050D8' }} />
                </Box>
              </Box>

              {/* Number and Trend Indicator Row */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 2,
                  mb: 1.5,
                }}
              >
                {isLoadingPriorityCounts || isLoadingSeverityPercentage ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Loading size="2rem" />
                  </Box>
                ) : (
                  <>
                    <Typography
                      sx={{
                        fontSize: '32px',
                        fontWeight: 700,
                        color: '#333',
                        lineHeight: 1.2,
                      }}
                    >
                      {priorityStats.low.count}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                      <Typography
                        sx={{
                          fontSize: '20px',
                          fontWeight: 600,
                          color: priorityStats.low.change > 0 ? '#D32F2F' : '#2E7D32',
                        }}
                      >
                        {priorityStats.low.change > 0 ? (
                          <ArrowUpward sx={{ fontSize: 20 }} />
                        ) : (
                          <ArrowDownward sx={{ fontSize: 20 }} />
                        )}
                        {Math.abs(priorityStats.low.change)}%
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>

              {/* Graph and Trend Label Row */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: 1,
                  width: '65%',
                }}
              >
                <Box sx={{ flex: 1, height: 50 }}>
                  <TrendGraph data={trendGraphLow?.data} color="#2050D8" gradientId="lowPriorityGradient" />
                </Box>
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: 400,
                    color: '#999',
                    mb: 0.5,
                  }}
                >
                  Last 7d trend
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Total Issues Card */}
          <Paper
            sx={{
              flex: 1,
              minWidth: { xs: '100%', sm: 200, md: 200 },
              borderRadius: 2,
              boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
              backgroundColor: '#FFFFFF',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Vertical colored bar on left edge */}
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '6px',
                backgroundColor: '#2E7D32',
              }}
            />
            <Box sx={{ p: 2, pl: 3 }}>
              {/* Top row: Title and Icon Button */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '18px',
                    fontStyle: 'bold',
                    fontWeight: 700,
                    color: '#666',
                  }}
                >
                  Total Issues
                </Typography>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    backgroundColor: '#2E7D321F',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #2E7D324D',
                  }}
                >
                  <ErrorOutline sx={{ fontSize: 24, color: '#2E7D32' }} />
                </Box>
              </Box>

              {/* Number and Trend Indicator Row */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 2,
                  mb: 1.5,
                }}
              >
                {isLoadingPriorityCounts || isLoadingSeverityPercentage ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Loading size="2rem" />
                  </Box>
                ) : (
                  <>
                    <Typography
                      sx={{
                        fontSize: '32px',
                        fontWeight: 700,
                        color: '#333',
                        lineHeight: 1.2,
                      }}
                    >
                      {priorityStats.total.count}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                      <Typography
                        sx={{
                          fontSize: '20px',
                          fontWeight: 600,
                          color: priorityStats.total.change > 0 ? '#D32F2F' : '#2E7D32',
                        }}
                      >
                        {priorityStats.total.change > 0 ? (
                          <ArrowUpward sx={{ fontSize: 20 }} />
                        ) : (
                          <ArrowDownward sx={{ fontSize: 20 }} />
                        )}
                        {Math.abs(priorityStats.total.change)}%
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>

              {/* Graph and Trend Label Row */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: 1,
                  width: '65%',
                }}
              >
                <Box sx={{ flex: 1, height: 50 }}>
                  <TrendGraph data={trendGraphTotal?.data} color="#2E7D32" gradientId="totalIssuesGradient" />
                </Box>
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: 400,
                    color: '#999',
                    mb: 0.5,
                  }}
                >
                  Last 7d trend
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Search, Filters, and Export Row */}
        <Box
          sx={{
            width: '100%',
            border: '1px solid',
            borderRadius: '12px',
            color: '#FFFFFF',
            backgroundColor: '#FFFFFF',
            boxShadow: '0px 0px 4px 0px #00000040',
            position: 'sticky',
            top: 0,
            zIndex: zIndexes.tableHeader,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1.5, sm: 2 },
              mb: { xs: 2, sm: 3 },
              flexWrap: 'wrap',
              flexDirection: { xs: 'column', sm: 'row' },
              paddingTop: { xs: '16px', sm: '24px' },
              paddingLeft: { xs: '16px', sm: '24px' },
              paddingRight: { xs: '16px', sm: '24px' },
              paddingBottom: { xs: '8px', sm: '4px' },
            }}
          >
            <TextField
              size="small"
              placeholder="Search Issues"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Search sx={{ color: '#9E9E9E' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: { xs: '100%', sm: 300 },
                width: { xs: '100%', sm: 'auto' },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  borderColor: '1px solid rgba(0, 0, 0, 0.23)',
                  '& fieldset': {
                    borderColor: '1px solid rgba(0, 0, 0, 0.23)',
                    borderWidth: '1px',
                  },
                  '&:hover fieldset': {
                    borderColor: '#000000',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#026AB7',
                  },
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#666',
                  opacity: 1,
                },
              }}
            />

            <FormControl
              size="small"
              sx={{
                minWidth: { xs: '100%', sm: 180 },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <InputLabel>Environment</InputLabel>
              <Select value={envFilter} label="Environment" onChange={(e) => setEnvFilter(e.target.value)}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="prod">Prod</MenuItem>
                <MenuItem value="dev">Dev</MenuItem>
                <MenuItem value="qa">QA</MenuItem>
                <MenuItem value="uat">UAT</MenuItem>
                <MenuItem value="tst">TST</MenuItem>
                <MenuItem value="local">Local</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <DateRangePicker
                startDate={dateRange[0]}
                endDate={dateRange[1]}
                onDateChange={(start, end) => {
                  setDateRange([start, end])
                }}
              />
            </Box>

            <Box
              sx={{
                flexGrow: { xs: 0, sm: 1 },
                width: { xs: '100%', sm: 'auto' },
              }}
            />

            <Button
              variant="contained"
              color="primary"
              endIcon={<ArrowDropDown />}
              onClick={handleExportClick}
              disabled={isExporting}
              sx={{
                textTransform: 'none',
                px: 2,
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              {isExporting ? 'Exporting...' : 'Export'}
            </Button>
            <Menu
              anchorEl={exportMenuAnchor}
              open={Boolean(exportMenuAnchor)}
              onClose={handleExportClose}
              PaperProps={{
                sx: {
                  borderRadius: 1,
                  mt: 0.5,
                  minWidth: 150,
                },
              }}
            >
              <MenuItem
                onClick={handleExportXLSX}
                sx={{
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5,
                  }}
                >
                  <img src={xlsxIcon} alt="XLSX" />
                </Box>
                <Typography sx={{ fontSize: '14px', color: '#333' }}>XLSX</Typography>
              </MenuItem>
              <MenuItem
                onClick={handleExportCSV}
                sx={{
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5,
                  }}
                >
                  <img src={csvIcon} alt="CSV" />
                </Box>
                <Typography sx={{ fontSize: '14px', color: '#333' }}>CSV</Typography>
              </MenuItem>
            </Menu>
          </Box>

          {/* Table */}
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: 1,
              borderRadius: 1,
              overflow: 'auto',
              maxHeight: {
                xs: 'calc(100vh - 400px)',
                md: 'calc(100vh - 300px)',
              },
              overflowX: 'auto',
            }}
          >
            <Table sx={{ tableLayout: 'fixed', minWidth: 1400 }}>
              <TableHead
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: zIndexes.tableSubHeader,
                  backgroundColor: '#FFFFFF',
                }}
              >
                <TableRow sx={{ letterSpacing: '0.4px' }}>
                  <TableCell sx={{ fontSize: '14px', width: '35%', paddingLeft: '30px' }}>
                    <TableSortLabel
                      active={orderBy === 'title'}
                      direction={orderBy === 'title' ? order : 'asc'}
                      onClick={() => handleRequestSort('title')}
                      sx={{
                        '& .MuiTableSortLabel-icon': {
                          opacity: orderBy === 'title' ? 1 : 0.3,
                          fontSize: '20px',
                          color: '#000000',
                        },
                        fontWeight: 700,
                        fontSize: '14px',
                        lineHeight: '143%',
                        color: '#333',
                        letterSpacing: '0.4px',
                      }}
                    >
                      Issue
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '14px',
                      width: '10%',
                      fontWeight: 700,
                      lineHeight: '143%',
                      color: '#333',
                      letterSpacing: '0.4px',
                    }}
                  >
                    Environment
                  </TableCell>
                  <TableCell sx={{ fontSize: '14px', width: '12%' }}>
                    <TableSortLabel
                      active={orderBy === 'lastSeen'}
                      direction={orderBy === 'lastSeen' ? order || 'asc' : 'asc'}
                      onClick={() => handleRequestSort('lastSeen')}
                      sx={{
                        '& .MuiTableSortLabel-icon': {
                          opacity: orderBy === 'lastSeen' ? 1 : 0.3,
                          fontSize: '20px',
                          color: '#000000',
                        },
                        fontWeight: 700,
                        fontSize: '14px',
                        lineHeight: '143%',
                        color: '#333',
                        letterSpacing: '0.4px',
                      }}
                    >
                      LastSeen
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontSize: '14px', width: '8%' }}>
                    <TableSortLabel
                      active={orderBy === 'age'}
                      direction={orderBy === 'age' ? order : 'asc'}
                      onClick={() => handleRequestSort('age')}
                      sx={{
                        '& .MuiTableSortLabel-icon': {
                          opacity: orderBy === 'age' ? 1 : 0.3,
                          fontSize: '20px',
                          color: '#000000',
                        },
                        fontWeight: 700,
                        fontSize: '14px',
                        lineHeight: '143%',
                        color: '#333',
                        letterSpacing: '0.4px',
                      }}
                    >
                      Age
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontSize: '14px', width: '8%', textAlign: 'right' }}>
                    <TableSortLabel
                      active={orderBy === 'totalEvents'}
                      direction={orderBy === 'totalEvents' ? order : 'asc'}
                      onClick={() => handleRequestSort('totalEvents')}
                      sx={{
                        '& .MuiTableSortLabel-icon': {
                          opacity: orderBy === 'totalEvents' ? 1 : 0.3,
                          fontSize: '20px',
                          color: '#000000',
                        },
                        fontWeight: 700,
                        fontSize: '14px',
                        lineHeight: '143%',
                        color: '#333',
                        letterSpacing: '0.4px',
                      }}
                    >
                      Events
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontSize: '14px', width: '8%', textAlign: 'right' }}>
                    <TableSortLabel
                      active={orderBy === 'affectedUsers'}
                      direction={orderBy === 'affectedUsers' ? order : 'asc'}
                      onClick={() => handleRequestSort('affectedUsers')}
                      sx={{
                        '& .MuiTableSortLabel-icon': {
                          opacity: orderBy === 'affectedUsers' ? 1 : 0.3,
                          fontSize: '20px',
                          color: '#000000',
                          paddingRight: 'none',
                        },
                        fontWeight: 700,
                        fontSize: '14px',
                        lineHeight: '143%',
                        color: '#333',
                        letterSpacing: '0.4px',
                      }}
                    >
                      Users
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '14px',
                      width: '10%',
                      fontWeight: 700,
                      lineHeight: '143%',
                      color: '#333',
                      letterSpacing: '0.4px',
                      textAlign: 'center',
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'priority'}
                      direction={orderBy === 'priority' ? order : 'asc'}
                      onClick={() => handleRequestSort('priority')}
                      sx={{
                        '& .MuiTableSortLabel-icon': {
                          opacity: orderBy === 'priority' ? 1 : 0.3,
                          fontSize: '20px',
                          color: '#000000',
                        },
                        fontWeight: 700,
                        fontSize: '14px',
                        lineHeight: '143%',
                        color: '#333',
                        letterSpacing: '0.4px',
                      }}
                    >
                      Priority
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '14px',
                      width: '8%',
                      fontWeight: 700,
                      lineHeight: '143%',
                      color: '#333',
                      letterSpacing: '0.4px',
                      textAlign: 'center',
                    }}
                  >
                    Assignee
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={10} sx={{ textAlign: 'center', py: 8 }}>
                      <Loading label="Sentry issues" />
                    </TableCell>
                  </TableRow>
                ) : paginatedIssues.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} sx={{ textAlign: 'center', py: 8 }}>
                      <Typography variant="body1" sx={{ color: '#666', fontSize: '16px' }}>
                        No Sentry issues found
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#999', fontSize: '14px', mt: 1 }}>
                        There are currently no issues to display
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedIssues.map((issue) => (
                    <TableRow
                      key={issue.id}
                      hover
                      onClick={() => {
                        setSelectedIssue(issue)
                        setIsDetailPanelOpen(true)
                      }}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#f9f9f9',
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          width: '35%',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                          paddingLeft: '30px',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                fontSize: '14px',
                                lineHeight: '143%',
                                color: '#333',
                                letterSpacing: '0.4px',
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                              }}
                            >
                              {issue.title}
                            </Typography>
                          </Box>
                          {issue.isUnhandled && issue.subtitle && (
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '13px',
                                color: '#666',
                                fontWeight: 500,
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                                letterSpacing: '0.4px',
                                lineHeight: 1.4,
                              }}
                            >
                              {issue.subtitle}
                            </Typography>
                          )}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              flexWrap: 'wrap',
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '12px',
                                color: '#000000',
                                wordBreak: 'break-word',
                                letterSpacing: '0.4px',
                                overflowWrap: 'break-word',
                              }}
                            >
                              {issue.shortId}
                            </Typography>
                            {issue.isUnhandled && (
                              <Chip
                                label="Unhandled"
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '10px',
                                  backgroundColor: '#ffebee',
                                  color: '#c62828',
                                  fontWeight: 600,
                                  flexShrink: 0,
                                }}
                              />
                            )}
                            {issue.path && (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '11px',
                                  color: '#999',
                                  letterSpacing: '0.4px',
                                  wordBreak: 'break-word',
                                  overflowWrap: 'break-word',
                                }}
                              >
                                {issue.path}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ width: '10%' }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '12px',
                            color: '#333',
                            fontWeight: 600,
                          }}
                        >
                          {issue.enrichedEnvironment?.toUpperCase() || envFilter.toUpperCase()}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '12%' }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '12px',
                            color: '#333',
                            fontWeight: 600,
                          }}
                        >
                          {issue.lastSeen && issue.lastSeen !== 'Just now'
                            ? (() => {
                                // Show exact time from API without converting to local time
                                // Parse the ISO timestamp and format it as-is (UTC time)
                                return dayjs.utc(issue.lastSeen).format(DaylightDateFormat.DATE_TIME_12)
                              })()
                            : issue.lastSeen || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '8%' }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '12px',
                            color: '#333',
                            fontWeight: 600,
                          }}
                        >
                          {issue.age}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '8%', textAlign: 'right' }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '12px',
                            color: '#333',
                            fontWeight: 600,
                          }}
                        >
                          {formatNumber(issue.totalEvents)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '8%', textAlign: 'right' }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '12px',
                            color: '#333',
                            fontWeight: 600,
                          }}
                        >
                          {issue.affectedUsers}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '10%', textAlign: 'center' }}>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            paddingLeft: '6px',
                            paddingRight: '28px',
                            width: '90px',
                            py: '5px',
                            borderRadius: '12px',
                            backgroundColor:
                              issue.priority === 'high'
                                ? '#FFEBEE' // Light red background
                                : issue.priority === 'low'
                                  ? '#E3F2FD' // Light blue background
                                  : '#FFF9C4', // Light yellow background
                            color:
                              issue.priority === 'high'
                                ? '#B81C1C' // Red text
                                : issue.priority === 'low'
                                  ? '#2050D8' // Blue text
                                  : '#A16107', // Brown text for medium
                          }}
                        >
                          {issue.priority === 'high' ? (
                            <KeyboardArrowUp
                              sx={{
                                fontSize: 22,
                                color: '#B81C1C',
                                marginBottom: '2px',
                              }}
                            />
                          ) : issue.priority === 'low' ? (
                            <KeyboardArrowDown
                              sx={{
                                fontSize: 22,
                                color: '#2050D8',
                                marginBottom: '2px',
                              }}
                            />
                          ) : (
                            <Typography
                              sx={{
                                fontSize: 22,
                                color: '#A16107',
                                marginBottom: '2px',
                                paddingRight: '6px',
                              }}
                            >
                              =
                            </Typography>
                          )}
                          <Typography
                            sx={{
                              fontSize: '12px',
                              fontWeight: 600,
                              color:
                                issue.priority === 'high'
                                  ? '#B81C1C'
                                  : issue.priority === 'low'
                                    ? '#2050D8'
                                    : '#A16107',
                            }}
                          >
                            {issue.priority === 'high' ? 'High' : issue.priority === 'low' ? 'Low' : 'Medium'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ width: '10%' }}>
                        {issue.assignee ? (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 30,
                                height: 30,
                                backgroundColor: '#2E7D324D',
                                fontSize: 13,
                                fontWeight: 600,
                              }}
                            >
                              {issue.assignee?.charAt(0).toUpperCase() || 'A'}
                            </Avatar>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '12px',
                                color: '#333',
                                fontWeight: 600,
                              }}
                            >
                              Assignee
                            </Typography>
                          </Box>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '18px',
                              color: '#333',
                              fontWeight: 600,
                              textAlign: 'center',
                            }}
                          >
                            --
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={totalCount}
            page={currentPage}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 15, 25]}
            slotProps={{
              actions: {
                nextButton: {
                  disabled: !canGoToNextPage,
                },
              },
            }}
            sx={{
              borderTop: '1px solid #e0e0e0',
              '& .MuiTablePagination-toolbar': {
                px: 2,
              },
            }}
          />
        </Box>
      </Box>

      {/* Issue Detail Panel */}
      <IssueDetailPanel open={isDetailPanelOpen} onClose={() => setIsDetailPanelOpen(false)} issue={selectedIssue} />
    </Box>
  )
}
